import Button from '@alltrails/shared/denali/components/Button';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import CustomProvider from 'components/CustomProvider';
import PaymentFinePrint from 'components/PaymentFinePrint';
import React, { Component, ReactNode } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import * as formStyles from '../styles/styles.module.scss';
import CouponCode from '../CouponCode';
import CreditCardForm from '../CreditCardForm/CreditCardForm';
import PlanTypeCard from '../pro/PlanTypeCard';
import * as styles from './styles/UpdatePaymentForm.module.scss';
import hasValidationErrors from './utils/hasValidationErrors';

const error = <FormattedMessage defaultMessage="There was an error processing your payment. Please contact support." />;

type Props = {
  updateBillingUrl?: string;
  plan?: Plan;
  formData?: {
    billing_first_name?: string;
    billing_last_name?: string;
    cc_expiration_month?: number;
    cc_expiration_year?: number;
  };
  coupon?: Coupon;
  reactivation: boolean;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  languageRegionCode: LanguageRegionCode;
};

type State = {
  errors: any;
  name: string;
  number: string;
  cvv: string;
  postalCode: string;
  isCouponApplied: boolean;
  verifiedCoupon: Coupon;
  serverError: any;
  submitting: boolean;
  couponCode: string;
  couponError: ReactNode;
  isCouponInputExpanded: boolean;
  expiration: string;
  isCouponInputShown?: boolean;
};

class UpdatePaymentForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { billing_first_name, billing_last_name, cc_expiration_month, cc_expiration_year } = props.formData;

    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        postalCode: ''
      },
      name: `${billing_first_name} ${billing_last_name}`,
      number: '',
      cvv: '',
      postalCode: '',
      serverError: null,
      submitting: false,
      verifiedCoupon: this.props.coupon,
      couponCode: this.props.coupon ? this.props.coupon.code : null,
      isCouponApplied: false,
      couponError: '',
      isCouponInputExpanded: false,
      expiration: cc_expiration_month ? `${String(cc_expiration_month).padStart(2, '0')} / ${cc_expiration_year % 100}` : ''
    };
  }

  componentDidMount() {
    if (this.state.verifiedCoupon && this.props.reactivation) {
      this.setState({ isCouponApplied: true, isCouponInputExpanded: true, isCouponInputShown: true, submitting: false });
    }
  }

  handleCreditCardFormChange = (event: any) => {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, this.state.errors);

    switch (name) {
      case 'cvv':
        this.setState({ cvv: value, errors: payload.errors });
        break;
      case 'expiration':
        this.setState({ expiration: value, errors: payload.errors });
        break;
      case 'name':
        this.setState({ name: value, errors: payload.errors });
        break;
      case 'number':
        this.setState({ number: value, errors: payload.errors });
        break;
      case 'postalCode':
        this.setState({ postalCode: value, errors: payload.errors });
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  };

  handlePaymentEditSuccess = (data: any) => {
    window.location.href = LanguageSupportUtil.wrapUrlSafe(data.redirectUrl, this.props.languageRegionCode);
  };

  handleRemoveCoupon = () => {
    this.setState({
      isCouponInputExpanded: false,
      couponCode: null,
      isCouponApplied: false,
      verifiedCoupon: null
    });
  };

  handleCouponCodeChange = (event: any) => {
    const { value } = event.target;
    this.setState({ couponCode: value, couponError: '' });
  };

  handleApplyCoupon = () => {
    this.handleCouponVerification(this.state.couponCode);
  };

  handlePaymentFormError = (err: any) => {
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText).errors;
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = typeof errorObj.base[0] === 'string' ? errorObj.base[0] : error;
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }

      if (errorObj.cc_expiration_month) {
        errors.expiration = errorObj.cc_expiration_month[0];
      }

      if (errorObj.cc_expiration_year) {
        errors.expiration = errorObj.cc_expiration_year[0];
      }

      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
    } else {
      serverError = error;
    }

    this.setState({ errors, serverError, submitting: false });
  };

  getSubmissionData() {
    const [cc_expiration_month, cc_expiration_year] = this.state.expiration.split(' / ');

    if (this.props.reactivation) {
      return {
        recurly_plan: this.props.plan.recurlyPlanCode,
        user: {
          cc_number: this.state.number,
          cc_expiration_month,
          cc_expiration_year,
          cc_csc: this.state.cvv,
          zip: this.state.postalCode,
          user_entered_coupon_code: this.state.verifiedCoupon ? this.state.verifiedCoupon.code : null,
          reactivating: true
        }
      };
    }

    return {
      user: {
        cc_number: this.state.number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode
      }
    };
  }

  noGiftCard() {
    if (!this.props.reactivation || !this.state.verifiedCoupon) {
      return true;
    }

    return this.state.verifiedCoupon.type !== 'GiftCard';
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };

    validation = validatePaymentForm(this.state.errors.name, this.state.name, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validatePaymentForm(this.state.errors.number, this.state.number, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validatePaymentForm(this.state.errors.cvv, this.state.cvv, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validatePaymentForm(this.state.errors.postalCode, this.state.postalCode, this.state.errors);
    validationErrors = { ...validationErrors, ...validation.errors };

    this.setState({ errors: validationErrors });

    return hasValidationErrors(validationErrors);
  }

  submitCreditCardForm = (e: any) => {
    e.preventDefault();

    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });

    if (this.noGiftCard()) {
      if (this.failedFormValidation()) {
        this.setState({ submitting: false });
        return;
      }
    }

    this.setState({ errors: {} });

    const submissionData = this.getSubmissionData();

    ServerCommunicationUtil.putApiEndpoint(
      this.props.updateBillingUrl,
      submissionData,
      this.handlePaymentEditSuccess,
      this.handlePaymentFormError,
      () => {},
      { 'X-Language-Locale': this.props.languageRegionCode }
    );
  };

  handleCouponVerification = (couponCode: string) => {
    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });

    const payload = {
      coupon_code: couponCode
    };

    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      payload,
      (data: any) => {
        this.setState({ verifiedCoupon: data[0], isCouponApplied: true, submitting: false });
      },
      (err: any) => {
        this.setState({
          isCouponApplied: false,
          couponError: <FormattedMessage defaultMessage="This coupon code is invalid" />,
          submitting: false
        });
      },
      () => {}
    );
  };

  renderCreditCardForm() {
    return (
      <>
        {this.props.reactivation && (
          <div className={styles.billingText}>
            <FormattedMessage defaultMessage="Enter billing information" />
          </div>
        )}
        <form className={styles.form} id="update-payment-form" onSubmit={this.submitCreditCardForm}>
          <CreditCardForm
            changeHandler={this.handleCreditCardFormChange}
            cvv={this.state.cvv}
            errors={this.state.errors}
            expiration={this.state.expiration}
            isCouponInputExpanded={this.state.isCouponInputExpanded}
            isCouponInputShown={this.state.isCouponInputShown}
            name={this.state.name}
            number={this.state.number}
            postalCode={this.state.postalCode}
            setError={(name, error) => this.setState({ errors: { ...this.state.errors, [name]: error } })}
            toggleCouponCode={() => this.setState({ isCouponInputExpanded: !this.state.isCouponInputExpanded })}
          />
          {this.props.reactivation && (
            <CouponCode
              applyHandler={this.handleApplyCoupon}
              changeHandler={this.handleCouponCodeChange}
              errorMessage={this.state.couponError}
              expanded={this.state.isCouponInputExpanded}
              isSuccess={this.state.isCouponApplied}
              removeHandler={this.handleRemoveCoupon}
              value={this.state.couponCode}
            />
          )}
          {this.state.serverError && <div className={styles.serverError}>{this.state.serverError}</div>}
          <Button
            disabled={this.state.submitting}
            onClick={() => {
              /* do nothing - form handles submit */
            }}
            testId="update-payment-submit"
            text={<FormattedMessage defaultMessage="Save" />}
            type="submit"
            variant="primary"
            width="full"
          />
          {!this.props.reactivation && (
            <Button
              linkInfo={{ href: '/my/profile/edit' }}
              onClick={undefined}
              testId=""
              text={<FormattedMessage defaultMessage="Cancel" />}
              variant="flat"
            />
          )}
        </form>
      </>
    );
  }

  render() {
    return (
      <CustomProvider>
        <div className={styles.billingContainer}>
          <div className={styles.title}>
            {this.props.reactivation ? <FormattedMessage defaultMessage="Pick your plan" /> : <FormattedMessage defaultMessage="Credit card" />}
          </div>
          {this.props.reactivation && (
            <div className={formStyles.foot}>
              <PaymentFinePrint
                coupon={this.state.verifiedCoupon}
                currencyCode={this.props.currencyCode}
                isCouponApplied={this.props.isCouponApplied}
                plan={this.props.plan}
              />
            </div>
          )}
          {this.props.reactivation && (
            <>
              <PlanTypeCard
                plan={this.props.plan}
                isActive
                currencyCode={this.props.currencyCode}
                coupon={this.state.verifiedCoupon}
                isCouponApplied={this.state.isCouponApplied}
                extraClasses={styles.card}
              />
              <div className={styles.divider} />
            </>
          )}
          <div>
            <div>{this.renderCreditCardForm()}</div>
          </div>
        </div>
      </CustomProvider>
    );
  }
}

export default UpdatePaymentForm;
